import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getUserSubscription, cancelSubscription, getBillingHistory } from '../../../utils/stripeService';

const SubscriptionSettings = ({ subscription: propSubscription }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscription, setSubscription] = useState(propSubscription);
  const [billingHistory, setBillingHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const subscriptionPlans = [
    {
      id: 'premium',
      name: 'All Access Premium',
      price: 5,
      features: ['Access to 500+ games', 'Play on unlimited devices', 'New games added weekly', 'Cancel anytime'],
      current: subscription?.plan === 'premium'
    }
  ];

  useEffect(() => {
    const loadSubscriptionData = async () => {
      setIsLoading(true);
      try {
        // Load current subscription
        const subResult = await getUserSubscription();
        if (subResult.success && subResult.subscription) {
          setSubscription(subResult.subscription);
        }

        // Load billing history
        const historyResult = await getBillingHistory();
        if (historyResult.success) {
          setBillingHistory(historyResult.history);
        }
      } catch (error) {
        console.error('Error loading subscription data:', error);
        setError('Failed to load subscription data');
      } finally {
        setIsLoading(false);
      }
    };

    if (!propSubscription) {
      loadSubscriptionData();
    } else {
      // Load billing history even if subscription is provided
      getBillingHistory().then(result => {
        if (result.success) {
          setBillingHistory(result.history);
        }
      });
    }
  }, [propSubscription]);

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    if (!subscription?.stripe_subscription_id) {
      setError('No subscription found to cancel');
      return;
    }

    setIsLoading(true);
    try {
      const result = await cancelSubscription(subscription.stripe_subscription_id);
      if (result.success) {
        // Update local subscription state
        setSubscription(prev => ({
          ...prev,
          status: 'cancelled'
        }));
        setShowCancelModal(false);
      } else {
        setError(result.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      setError('Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return { text: 'No Subscription', className: 'bg-muted/10 text-muted-foreground' };
    
    const now = new Date();
    const periodEnd = new Date(subscription.current_period_end);
    
    if (subscription.status === 'active' && periodEnd > now) {
      return { text: 'Active', className: 'bg-green-500/10 text-green-600' };
    } else if (subscription.status === 'cancelled') {
      return { text: 'Cancelled', className: 'bg-red-500/10 text-red-600' };
    } else {
      return { text: 'Expired', className: 'bg-yellow-500/10 text-yellow-600' };
    }
  };

  if (isLoading && !subscription) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading subscription...</span>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center">
          <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Active Subscription</h3>
          <p className="text-muted-foreground mb-4">
            Subscribe to get unlimited access to all games
          </p>
          <Link to="/subscription-management">
            <Button>Start Subscription</Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getSubscriptionStatus();

  return (
    <>
      <div className="bg-card rounded-lg border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="CreditCard" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Subscription Management</h2>
          </div>
          <Link to="/subscription-management">
            <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
              Full Management
            </Button>
          </Link>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-sm text-destructive">{error}</span>
              </div>
            </div>
          )}

          {/* Current Subscription */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Current Plan: {subscription.plan === 'premium' ? 'All Access Premium' : subscription.plan}
                </h3>
                <p className="text-muted-foreground">
                  ${subscription.price_per_month}/month • Next billing: {formatDate(subscription.current_period_end)}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}>
                {statusInfo.text}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {subscriptionPlans[0].features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-1 text-sm text-muted-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <Link to="/subscription-management">
                <Button variant="outline" size="sm" iconName="Edit">
                  Manage Subscription
                </Button>
              </Link>
              {subscription.status === 'active' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  iconName="X"
                  onClick={handleCancelSubscription}
                  className="text-destructive hover:text-destructive"
                  disabled={isLoading}
                >
                  {isLoading ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              )}
            </div>
          </div>

          {/* Available Plans */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Available Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {subscriptionPlans.map((plan) => (
                <div key={plan.id} className={`border rounded-lg p-4 ${
                  plan.current 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                } transition-colors`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-foreground">{plan.name}</h4>
                    {plan.current && (
                      <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                        Current
                      </div>
                    )}
                  </div>
                  <div className="text-2xl font-bold text-foreground mb-3">
                    ${plan.price}<span className="text-sm font-normal text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-2 mb-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm">
                        <Icon name="Check" size={14} className="text-accent" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {!plan.current && (
                    <Link to="/subscription-management">
                      <Button variant="outline" size="sm" fullWidth>
                        {plan.price > subscription.price_per_month ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Payment History */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Payment History</h3>
            {billingHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left py-3 text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="text-left py-3 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-3 text-sm font-medium text-muted-foreground">Invoice</th>
                      <th className="text-right py-3 text-sm font-medium text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map((payment) => (
                      <tr key={payment.id} className="border-b border-border/50">
                        <td className="py-3 text-sm text-foreground">{formatDate(payment.date)}</td>
                        <td className="py-3 text-sm text-foreground">${payment.amount.toFixed(2)}</td>
                        <td className="py-3">
                          <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'paid' ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'
                          }`}>
                            {payment.status}
                          </div>
                        </td>
                        <td className="py-3 text-sm text-muted-foreground font-mono">{payment.invoice}</td>
                        <td className="py-3 text-right">
                          <Button variant="ghost" size="sm" iconName="Download">
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="Receipt" size={48} className="mx-auto mb-2 opacity-50" />
                <p>No payment history available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
              <h3 className="text-lg font-semibold text-foreground">Cancel Subscription</h3>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Are you sure you want to cancel your subscription? You'll lose access to all premium features at the end of your current billing period.
            </p>
            
            <div className="bg-muted/30 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-foreground mb-2">What you'll lose:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Access to premium games</li>
                <li>• 4K streaming quality</li>
                <li>• Multi-device support</li>
                <li>• Early access to new releases</li>
              </ul>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelModal(false)}
                fullWidth
              >
                Keep Subscription
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmCancellation}
                fullWidth
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionSettings;
